import type { ExternalMCPServerInput } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "../use-api-context"
import {
  authorizeExternalMCPOAuth,
  createExternalMCPServer,
  deleteExternalMCPServer,
  isExternalMCPManualAuthenticationRequired,
  updateExternalMCPServer,
} from "./api"

const QUERY_KEY = ["external-mcp-servers"]

export function useCreateExternalMCPServer() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: ExternalMCPServerInput) => {
      const context = await getContext()
      return createExternalMCPServer(context, input)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    meta: {
      errorToast: (error) => (isExternalMCPManualAuthenticationRequired(error) ? null : error.message),
    },
  })
}

export function useUpdateExternalMCPServer() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<ExternalMCPServerInput> }) => {
      const context = await getContext()
      return updateExternalMCPServer(context, id, input)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useDeleteExternalMCPServer() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      return deleteExternalMCPServer(context, id)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useAuthorizeExternalMCPOAuth() {
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async ({ id, returnUri }: { id: string; returnUri: string }) => {
      const context = await getContext()
      return authorizeExternalMCPOAuth(context, id, returnUri)
    },
  })
}
